import React, { useEffect, useState } from 'react';

import * as Yup from 'yup';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { Button, Card, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FileWithPath } from 'react-dropzone';
import { Link, useHistory } from 'react-router-dom';
import { boolean } from 'yup';
import { restValidationToForm, setYupLocale } from '../utils/formUtils';
import { fetchIndustries, fetchUnions, fetchEducationIndustries, fetchEducationOthers } from '../store/createApplicationSlice';
import ApplicationPersonCreate from '../components/applications/ApplicationPersonCreate';
import ApplicationEducationCreate from '../components/applications/ApplicationEducationCreate';
import ApplicationSignUpCreate from '../components/applications/ApplicationSignUpCreate';
import ApplicationJobsCreate from '../components/applications/ApplicationJobsCreate';
import ApplicationFilesUpload from '../components/applications/ApplicationFilesUpload';
import ApplicationOtherCreate from '../components/applications/ApplicationOtherCreate';
import { AppDispatch } from '../store/store';
import { RootState } from '../store/rootReducer';
import { ApplicationIndustry } from '../api/openapi';
import { applicationApi } from '../api';
import ApplicationINNAEducation from '../components/applications/ApplicationINNAEducation';

export interface CreateApplicationFormValues {
  acceptedTerms: boolean;
  industry: ApplicationIndustry | null;
  industryPensionFundRequired: boolean;
  school: string;
  ssn: string;
  name: string;
  address: string;
  email: string;
  mobile: string;
  union: string;
  hasNoUnion: boolean | undefined;
  dontKnowUnion: boolean | undefined;
  educationIndustry: {
    label: string;
    value: string;
  }[];
  educationOther: {
    label: string;
    value: string;
  }[];
  course: {
    name: string;
  }[];
  job: {
    job: string;
    company: string;
    description: string;
  }[];
  isMatriculationFinished: boolean | null;
  isJourneymanFinished: boolean | null;
  isContractFinished: boolean | null;
  isEducationAbroadFinished: boolean | null;
  hasOngoingContract: boolean | null;
  otherEducationInformation: string;
  otherInformation: string;
  isPensionfundFile: boolean | undefined;
  files: FileWithPath[];
  filesExtra: {
    isPensionFundFile: boolean;
  }[];
  isDraft: boolean;
}

const ApplicationCreate: React.FC = () => {
  const { t, i18n } = useTranslation(['createApplication', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();
  const [validationSchema, setValidationSchema] = useState<Yup.AnySchema | null>(null);
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    dispatch(fetchIndustries());
    dispatch(fetchUnions());
    dispatch(fetchEducationIndustries());
    dispatch(fetchEducationOthers());
  }, [dispatch]);

  useEffect(() => {
    setYupLocale(Yup, i18n.language);
    setValidationSchema(
      Yup.object().shape({
        isDraft: boolean(),
        industry: Yup.object().label(t('industry')).nullable().required(),
        industryPensionFundRequired: Yup.boolean().required(),
        school: Yup.string().label(t('school')).required(),
        ssn: Yup.string().label(t('ssn')).required(),
        name: Yup.string().label(t('name')).required(),
        email: Yup.string().label(t('applicantEmail')).required(),
        mobile: Yup.string().label(t('applicantMobile')).required(),
        educationIndustry: Yup.array()
          .of(
            Yup.object().shape({
              label: Yup.string(),
              value: Yup.string(),
            })
          )
          .label(t('educationIndustry'))
          .required()
          .when('isJourneymanFinished', (isJourneymanFinished: boolean, schema: Yup.ArraySchema<Yup.AnySchema>) => {
            return isJourneymanFinished ? schema.min(1, t('educationIndustryIsRequired')) : schema;
          }),
        job: Yup.array()
          .of(
            Yup.object().shape({
              id: Yup.string(),
              job: Yup.string().label(t('job')).required(),
              company: Yup.string().label(t('jobCompany')).required(),
            })
          )
          .label(t('job'))
          .required(),
        isMatriculationFinished: Yup.boolean()
          .nullable()
          .label(t('isMatriculationFinished'))
          .when('isDraft', (isDraft, schema) => (isDraft ? schema : schema.required())),
        isEducationAbroadFinished: Yup.boolean()
          .nullable()
          .label(t('isEducationAbroadFinished'))
          .when('isDraft', (isDraft, schema) => (isDraft ? schema : schema.required())),
        isJourneymanFinished: Yup.boolean()
          .nullable()
          .label(t('isJourneymanFinished'))
          .when('isDraft', (isDraft, schema) => (isDraft ? schema : schema.required())),
        isContractFinished: Yup.boolean()
          .nullable()
          .label(t('isContractFinished'))
          .when('isDraft', (isDraft, schema) => (isDraft ? schema : schema.required())),
        hasOngoingContract: Yup.boolean()
          .nullable()
          .label(t('hasOngoingContract'))
          .when('isDraft', (isDraft, schema) => (isDraft ? schema : schema.required())),
        // eslint-disable-next-line react/forbid-prop-types
        files: Yup.array(),
        filesExtra: Yup.array().when(['industryPensionFundRequired', 'isDraft'], {
          is: (industryPensionFundRequired: boolean, isDraft: boolean) => !industryPensionFundRequired || isDraft,
          then: Yup.array(),
          otherwise: Yup.array().test('has-pension-fund-file', t('onePensionfundFile'), (v) => !!v && v.filter((x) => x.isPensionFundFile).length > 0),
        }),
      })
    );
  }, [t, i18n.language]);

  const onSubmitApplication = (formik: FormikProps<CreateApplicationFormValues>, isDraft: boolean) => {
    formik.setFieldValue('isDraft', isDraft);

    if (isDraft) {
      const job = JSON.parse(JSON.stringify(formik.values.job)) as CreateApplicationFormValues['job'];

      formik.setFieldValue(
        'job',
        job.filter((j) => j.company || j.description || j.job)
      );
    }

    formik.setStatus('submitted');
    setTimeout(() => {
      formik.handleSubmit();
    });
  };

  if (currentUser == null) {
    return null;
  }

  return (
    <>
      <Formik<CreateApplicationFormValues>
        initialValues={{
          acceptedTerms: false,
          industry: null,
          industryPensionFundRequired: false,
          school: '',
          name: currentUser.name,
          ssn: currentUser.username,
          address: '',
          email: '',
          mobile: '',
          union: '',
          hasNoUnion: undefined,
          dontKnowUnion: undefined,
          otherEducationInformation: '',
          otherInformation: '',
          isMatriculationFinished: null,
          isJourneymanFinished: null,
          isContractFinished: null,
          hasOngoingContract: null,
          isEducationAbroadFinished: null,
          educationIndustry: [],
          educationOther: [],
          course: [
            {
              name: '',
            },
          ],
          job: [
            {
              job: '',
              company: '',
              description: '',
            },
          ],
          isPensionfundFile: undefined,
          files: [],
          filesExtra: [],
          isDraft: true,
        }}
        validateOnBlur={false}
        // validateOnChange={false}
        validationSchema={validationSchema}
        // enableReinitialize
        onSubmit={async (values: CreateApplicationFormValues, { setSubmitting, setFieldError }: FormikHelpers<CreateApplicationFormValues>) => {
          try {
            setIsLoading(true);
            const response = await applicationApi.myApplicationCreate({
              myApplicationDetailRequest: {
                email: values.email,
                address: values.address,
                mobile: values.mobile,
                union: values.union
                  ? {
                      id: values.union,
                    }
                  : null,
                hasNoUnion: values.hasNoUnion,
                dontKnowUnion: values.dontKnowUnion,
                industry: {
                  id: values.industry?.id || '',
                },
                school: {
                  id: Number(values.school),
                },
                isMatriculationFinished: values.isMatriculationFinished != null ? values.isMatriculationFinished : null,
                isContractFinished: values.isContractFinished != null ? values.isContractFinished : null,
                isJourneymanFinished: values.isJourneymanFinished != null ? values.isJourneymanFinished : null,
                hasOngoingContract: values.hasOngoingContract != null ? values.hasOngoingContract : null,
                isEducationAbroadFinished: values.isEducationAbroadFinished != null ? values.isEducationAbroadFinished : null,
                otherEducationInformation: values.otherEducationInformation,
                otherInformation: values.otherInformation,
                education: values.educationIndustry.map((e) => ({
                  educationIndustry: {
                    id: e.value,
                  },
                })),
                educationOther: values.educationOther.map((e) => ({
                  educationOther: {
                    id: e.value,
                  },
                })),
                course: values.course
                  .filter((e) => !!e.name)
                  .map((e) => ({
                    name: e.name,
                  })),
                job: values.job.map((e) => ({
                  job: e.job,
                  company: e.company,
                  description: e.description,
                })),
              },
            });

            await Promise.all(
              values.files.map((f, idx) =>
                applicationApi.applicationMyApplicationUploadFile({
                  uuid: response.data.id,
                  name: f.name,
                  file: f,
                  isPensionfundFile: values.filesExtra[idx].isPensionFundFile,
                })
              )
            );

            if (!values.isDraft) {
              await applicationApi.applicationMyApplicationComplete({
                uuid: response.data.id,
              });
            }

            history.push('/');
            toast.success(t('applicationCreated'));
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (e: any) {
            restValidationToForm({
              t,
              errorResponse: e.response,
              setFieldError,
              errorFieldMap: {
                name: 'name',
                ssn: 'ssn',
                address: 'address',
                email: 'email',
                mobile: 'mobile',
                union: 'union',
                industry: 'industry',
                school: 'school',
              },
            });
          } finally {
            setIsLoading(false);
            setSubmitting(false);
          }
        }}
      >
        {(formik): React.ReactElement => (
          <Form
            noValidate
            // onReset={handleReset}
            onSubmit={undefined}
          >
            <ApplicationSignUpCreate />
            {formik.values.acceptedTerms && (
              <>
                <ApplicationPersonCreate />

                <ApplicationINNAEducation />

                <ApplicationEducationCreate />

                <ApplicationJobsCreate />

                <ApplicationFilesUpload />

                <ApplicationOtherCreate />

                <Card className="mb-2">
                  <Card.Body className="text-right">
                    <Link to="/" className="btn btn-secondary">
                      {t('common:cancel')}
                    </Link>{' '}
                    <OverlayTrigger placement="right-end" overlay={<Tooltip id="tooltip-other">{t('saveApplicationFinishLater')}</Tooltip>}>
                      <Button type="button" variant="primary" onClick={() => onSubmitApplication(formik, true)}>
                        {t('common:save')}
                      </Button>
                    </OverlayTrigger>{' '}
                    <Button variant="success" type="button" onClick={() => onSubmitApplication(formik, false)} disabled={isLoading}>
                      {t('submit')}
                    </Button>
                  </Card.Body>
                </Card>
              </>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ApplicationCreate;
